import React, { useEffect, useMemo, useState } from 'react';
import { fetchUsers, updateUser } from '../api';
import { USER_GROUPS } from '../../config/permissions';
import './Users.css';
import CreateUserModal from './Modals/CreateUserModal';

const emptyForm = { user_id: '', name: '', email: '', group: '' };

function Users() {
  const [users, setUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [action, setAction] = useState('create');
  const [isDeleting, setIsDeleting] = useState(false);

  const groupOptions = useMemo(() => {
    return Object.values(USER_GROUPS).filter(g => g !== USER_GROUPS.MASTER && g !== USER_GROUPS.ADMIN);
  }, []);

  const filteredUsers = useMemo(() => {
    if (!search) return users;
    const q = search.toLowerCase();
    return users.filter(u => `${u.name || ''} ${u.email || ''}`.toLowerCase().includes(q));
  }, [users, search]);

  const loadUsers = async () => {
    setIsLoading(true);
    setError('');
    try {
      const data = await fetchUsers();
      setUsers(Array.isArray(data) ? data : []);
    } catch (e) {
      setError('Falha ao carregar usuários');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadUsers();
  }, []);

  const openCreate = () => {
    setForm(emptyForm);
    setAction('create');
    setIsModalOpen(true);
  };

  const openEdit = (user) => {
    setSelected(user);
    setForm({
      user_id: user.user_id,
      name: user.name || '',
      email: user.email || '',
      group: (user.groups || []).find(g => g !== USER_GROUPS.MASTER && g !== USER_GROUPS.ADMIN) || ''
    });
    setAction('update');
    setIsModalOpen(true);
  };

  const openDelete = (user) => {
    setSelected(user);
    setAction('delete');
    setIsModalOpen(true);
    setIsDeleting(false);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelected(null);
    setAction('create');
    setForm(emptyForm);
    setIsDeleting(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      let payload;
      if (action === 'delete') {
        setIsDeleting(true);
        payload = { action: 'delete', email: selected?.email };
      } else {
        const groupsObject = form.group ? { [form.group]: { display: form.group } } : {};
        payload = {
          action: action === 'create' ? 'create' : 'update',
          user_id: form.user_id,
          name: form.name,
          email: form.email,
          groups: groupsObject
        };
      }
      await updateUser(payload);
      await loadUsers();
      closeModal();
    } catch (err) {
      alert(err.message || 'Erro ao salvar');
    } finally {
      if (action === 'delete') setIsDeleting(false);
    }
  };

  return (
    <div className="users-page">
      <div className="users-header">
        <h2>Usuários</h2>
        <div className="users-actions">
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar por nome ou e-mail"
            className="users-search"
          />
          <button onClick={openCreate} className="primary">Novo usuário</button>
        </div>
      </div>

      {isLoading ? (
        <div className="loading">Carregando...</div>
      ) : error ? (
        <div className="error">{error}</div>
      ) : (
        <div className="users-table-wrapper">
          <table className="users-table">
            <thead>
              <tr>
                <th>Nome</th>
                <th>E-mail</th>
                <th>Grupos</th>
                
                <th>Ações</th>
              </tr>
            </thead>
            <tbody>
              {filteredUsers.map((u) => (
                <tr key={u.user_id}>
                  <td>{u.name}</td>
                  <td>{u.email}</td>
                  <td>{Array.isArray(u.groups) ? u.groups.join(', ') : ''}</td>
                  
                  <td>
                    {/* <button onClick={() => openEdit(u)} className="secondary">Editar</button> */}
                    <button onClick={() => openDelete(u)} className="danger">Excluir</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {isModalOpen && action === 'create' && (
        <CreateUserModal
          isOpen={isModalOpen}
          onClose={closeModal}
          onSubmit={async (payload) => {
            try {
              await updateUser(payload);
              await loadUsers();
              closeModal();
            } catch (err) {
              alert(err.message || 'Erro ao criar usuário');
            }
          }}
        />
      )}

      {isModalOpen && action !== 'create' && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal modal-elevated" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{action === 'update' ? 'Editar usuário' : 'Excluir usuário'}</h3>
              <button className="close" onClick={closeModal}>×</button>
            </div>

            {action === 'delete' ? (
              <div className="modal-body">
                <p>Confirmar exclusão de {selected?.name || selected?.email}?</p>
                <div className="modal-footer">
                  <button onClick={closeModal}>Cancelar</button>
                  <button className="danger" onClick={handleSubmit} disabled={isDeleting}>{isDeleting ? 'Excluindo...' : 'Excluir'}</button>
                </div>
              </div>
            ) : (
              <form className="modal-body" onSubmit={handleSubmit}>
                <label>
                  Nome
                  <input
                    type="text"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    required
                  />
                </label>
                <label>
                  E-mail
                  <input
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    required
                  />
                </label>
                <label>
                  Grupo
                  <select
                    value={form.group}
                    onChange={(e) => setForm({ ...form, group: e.target.value })}
                    required
                  >
                    <option value="">Selecione</option>
                    {groupOptions.map((g) => (
                      <option key={g} value={g}>{g}</option>
                    ))}
                  </select>
                </label>
                
                <div className="modal-footer">
                  <button type="button" onClick={closeModal}>Cancelar</button>
                  <button type="submit" className="primary">Salvar</button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default Users;


